import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { Icon } from '@holmescorp/base-widgets';
import { Keys } from '@holmescorp/common';
import { childmap, keymap, lccstate, pathmap } from '@holmescorp/lcc-state';

import css from '../css/study.m.css';
import { MicroListView } from './list-view';
import { MicroCardsView } from './mc-cards';

export interface StudyViewICache {
	cardView: boolean;
	listView: boolean;
	expandedView: boolean;
}

const factory = create({
	icache: createICacheMiddleware(),
	theme,
	lccstate
});

export const StudyView = factory(({ id, middleware: { icache, theme, lccstate } }) => {
	const fixedCss = theme.classes(css);
	const { get, path } = lccstate;

	const roots: string[] = get(path('ditaroots'));

	const tgfkeys = roots.flatMap((root) => childmap.get(root) || []);

	const topicsets = {
		attributes: { key: `mc-content-view` },
		children: tgfkeys.map((tgf) => {
			const { path } = keymap.get(tgf)!;
			const { topic } = pathmap.get(path)!;
			return { ...topic };
		})
	};

	const kids = topicsets.children?.map((ch) => ch.topicset) || [];

	const setView = (view: string) => {
		['cardView', 'listView'].forEach((v) => {
			icache.set(v, v === view);
		});
	};

	const viewCards = icache.getOrSet('cardView', true);
	const viewList = icache.getOrSet('listView', false);
	return v('div', { id, classes: [theme.variant(), fixedCss.root] }, [
		v('div', { xqa: 'study-legend', key: 'study-legend', classes: [fixedCss.legend] }, [
			v(
				'div',
				{
					id: 'cardView',
					role: 'button',
					...formatAriaProperties({
						pressed: viewCards.toString(),
						label: 'Card View'
					}),
					tabIndex: 0,
					onclick: (): void => {
						setView('cardView');
					},
					onkeydown: (event: KeyboardEvent) => {
						switch (event.key) {
							case Keys.Enter:
							case Keys.Space:
							case Keys.Spacebar:
								event.preventDefault();
								setView('cardView');
						}
					},
					classes: [fixedCss.legendItem]
				},
				[
					w(Icon, {
						...formatAriaProperties({ labeledBy: 'cardView' }),
						faname: 'table-cells-large',
						size: 'xl'
					})
				]
			),
			v(
				'div',
				{
					id: 'listView',
					role: 'button',
					...formatAriaProperties({
						pressed: viewList.toString(),
						label: 'List View'
					}),
					tabIndex: 0,
					onclick: (): void => {
						setView('listView');
					},
					onkeydown: (event: KeyboardEvent) => {
						switch (event.key) {
							case Keys.Enter:
							case Keys.Space:
							case Keys.Spacebar:
								event.preventDefault();
								setView('listView');
						}
					},
					classes: [fixedCss.legendItem]
				},
				[w(Icon, { ...formatAriaProperties({ labeledBy: 'listView' }), faname: 'list', size: 'xl' })]
			)
		]),
		v('div', { xqa: 'study-content', key: 'study-content', classes: [fixedCss.content] }, [
			viewCards
				? v('div', { classes: [fixedCss.cards] }, [w(MicroCardsView, { topics: kids })])
				: viewList
				? w(MicroListView, {
						key: 'study-content-list-view',
						xqa: 'study-content-list-view',
						topics: kids
				  })
				: null
		])
	]);
});

export default StudyView;
